import { Component } from "react"

/* Portal */
import { createPortal } from "react-dom"

/* Icons */
import { CheckIcon, ErrorIcon, WarningIcon, InfoIcon, CloseIcon } from "./Icons"


/* HOW MANY TOASTS STAY ON SCREEN AT ONCE */
const LIMIT = 4

/* HOW LONG THE HIDE ANIMATION RUNS (KEEP IT IN SYNC WITH toast.css) */
const ANIMATION = 220

/* ICON OF EVERY TYPE */
const ICONS = {
    success: CheckIcon,
    error: ErrorIcon,
    warning: WarningIcon,
    info: InfoIcon,
}


/* THE MOUNTED HOST — THE HELPER PUSHES THROUGH IT */
let host = null

/* AUTOINCREMENT OF THE TOAST ID */
let counter = 0


/* SHOW A TOAST FROM ANYWHERE (helpers/alert.js) */
export const pushToast = ({ text, type = "info", timer = 2600 }) => {

    /* Check Host */
    if (!host) {
        return
    }

    /* Next ID */
    counter += 1

    /* Show */
    host.add({ id: counter, text, type: ICONS[type] ? type : "info", timer })
}


/* Component Toast */
class Toast extends Component {

    constructor() {
        super()

        this.state = {
            list: [],
        }

        /* Hide timers by toast id */
        this.timers = {}
    }

    componentDidMount = () => {
        host = this
    }

    componentWillUnmount = () => {

        /* Release Host */
        host = null

        /* Clear Timers */
        Object.keys(this.timers).forEach((key) => clearTimeout(this.timers[key]))
        this.timers = {}
    }

    /* Add Toast — the oldest ones are pushed out */
    add = (item) => {

        this.setState((prev) => ({ list: [...prev.list, item].slice(-LIMIT) }), () => {
            this.prune()
            this.start(item.id, item.timer)
        })

    }

    /* Drop The Timers Of The Toasts That Are Gone */
    prune = () => {

        /* Fields */
        const { list } = this.state

        /* Clear Timers */
        Object.keys(this.timers).forEach((key) => {

            /* Toast ID */
            const id = key.replace("hide-", "")

            /* Check List */
            if (!list.some((item) => `${item.id}` === id)) {
                clearTimeout(this.timers[key])
                delete this.timers[key]
            }
        })

    }

    /* Start The Hide Timer */
    start = (id, timer) => {
        this.timers[id] = setTimeout(() => this.hide(id), timer)
    }

    /* Stop The Hide Timer — the pointer is over the toast */
    stop = (id) => {
        clearTimeout(this.timers[id])
        delete this.timers[id]
    }

    /* Hide With The Animation And Remove Afterwards */
    hide = (id) => {

        /* Clear Timer */
        this.stop(id)

        /* Mark As Hidden */
        this.setState((prev) => ({ list: prev.list.map((item) => item.id === id ? { ...item, hidden: true } : item) }))

        /* Remove */
        this.timers[`hide-${id}`] = setTimeout(() => {
            delete this.timers[`hide-${id}`]
            this.setState((prev) => ({ list: prev.list.filter((item) => item.id !== id) }))
        }, ANIMATION)

    }

    render = () => {

        /* Fields */
        const { list } = this.state

        /* Check List */
        if (list.length === 0) {
            return null
        }

        return createPortal(
            <div className="toast--host">

                {list.map((item) => {

                    /* Type Icon */
                    const TypeIcon = ICONS[item.type]

                    return (
                        <div onMouseEnter={() => this.stop(item.id)}
                            onMouseLeave={() => !item.hidden && this.start(item.id, item.timer)}
                            className={`toast ${item.type} ${item.hidden ? "hidden" : ""}`}
                            key={`${item.id}`}>

                            <div className="toast--icon">
                                <TypeIcon />
                            </div>

                            {/* The text comes from the translation files, so the markup inside it is ours */}
                            <div className="toast--text" dangerouslySetInnerHTML={{ __html: item.text }} />

                            <div onClick={() => this.hide(item.id)} className="toast--close">
                                <CloseIcon size={13} />
                            </div>

                            <div className="toast--line" style={{ animationDuration: `${item.timer}ms` }} />

                        </div>
                    )
                })}

            </div>,
            document.body,
        )
    }

}

export default Toast
